// BrowseInviteCreators.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  Image,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Dimensions,
  RefreshControl,
  TextInput,
  SafeAreaView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { Linking } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

const TOP_BADGE = new Set(['AAA', 'AA', 'A']);

// Simplified follower buckets
const FOLLOWER_BUCKETS = [
  { key: '10k–25k', min: 10000, max: 25000 },
  { key: '25k–50k', min: 25000, max: 50000 },
  { key: '50k–100k', min: 50000, max: 100000 },
  { key: '100k–300k', min: 100000, max: 300000 },
  { key: '300k+', min: 300000, max: Infinity },
];

const parseFollowers = (str = '') => {
  const s = String(str).toLowerCase().replace(/,/g, '').trim();
  if (s.endsWith('k')) return parseFloat(s) * 1000;
  if (s.endsWith('m')) return parseFloat(s) * 1_000_000;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

// Show up to 50 creators after filters/sort
const MAX_RENDER = 75;
const FOR_YOU_PAGE_SIZE = 4;

export default function BrowseInviteCreators() {
  const navigation = useNavigation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');

  // Raw data
  const [suggested, setSuggested] = useState([]);
  const [pool, setPool] = useState([]);
  const mounted = useRef(false);

  // Filters
  const [categories, setCategories] = useState(['All']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [bucket, setBucket] = useState(null);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [suggestedOnly, setSuggestedOnly] = useState(false);
  const [query, setQuery] = useState('');

  // Sort
  const [sort, setSort] = useState('shuffle'); // followers_desc | followers_asc | az | shuffle

  // "For you" window index (start index in suggested array)
  const [suggestStart, setSuggestStart] = useState(0);

  // Session + auth for sidenav + page gating
  const [userData, setUserData] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false); // used for invite gating
  const [isAuthed, setIsAuthed] = useState(false); // for sidenav lock
  const [authMode, setAuthMode] = useState('none'); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  const fetchAll = async () => {
    const dd = new Date();
    const ymd = dd.toISOString().slice(0, 10).replace(/-/g, ''); // cache-buster
    const urls = [
      `https://collabbayadmin.com/APIControllers/GetSuggestedUsersForBrand.php?d=${ymd}`,
      `https://collabbayadmin.com/APIControllers/GetVenueBrowseInviteOptions.php?d=${ymd}`,
    ];
    const [r1, r2] = await Promise.all([
      fetch(urls[0], { method: 'GET', credentials: 'include' }),
      fetch(urls[1], { method: 'GET', credentials: 'include' }),
    ]);
    if (!r1.ok) throw new Error(`Suggested HTTP ${r1.status}`);
    if (!r2.ok) throw new Error(`Pool HTTP ${r2.status}`);
    const j1 = await r1.json();
    const j2 = await r2.json();

    // Suggested may come in "data" or "users" depending on your version; prefer "data"
    const s = Array.isArray(j1?.data)
      ? j1.data
      : Array.isArray(j1?.users)
      ? j1.users
      : [];
    const p = Array.isArray(j2?.pool)
      ? j2.pool
      : Array.isArray(j2?.data)
      ? j2.data.filter((i) => !i.isSuggested)
      : [];

    // Mark suggested if endpoint didn't
    const sMarked = s.map((x) => ({ ...x, isSuggested: true }));

    // One-time shuffle for extra freshness each fetch
    const shuffleArr = (arr) => {
      const a = arr.slice();
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    const sShuffled = shuffleArr(sMarked);
    const pShuffled = shuffleArr(p);

    setSuggested(sShuffled);
    setPool(pShuffled);
    setSuggestStart(0); // reset window to start

    // Build categories
    const uniqueCats = [
      'All',
      ...Array.from(
        new Set(
          [...sShuffled, ...pShuffled]
            .map((i) => i.ContentCategories)
            .filter(Boolean)
        )
      ),
    ];
    setCategories(uniqueCats);
  };

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        await fetchAll();
        setLoading(false);
      } catch (e) {
        console.error(e);
        setErr('Could not load creators right now.');
        setSuggested([]);
        setPool([]);
        setCategories(['All']);
        setLoading(false);
      }
    })();
  }, []);

  // Pull-to-refresh
  const onRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchAll();
    } catch (e) {
      console.error(e);
      setErr('Refresh failed. Try again.');
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch session data for username pill
  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => setUserData(data))
      .catch((err) =>
        console.error('Error fetching session user data:', err)
      );
  }, []);

  // Auth state for sidenav + invite gating (single call, GET as per sidenav pattern)
  useEffect(() => {
    fetch(
      'https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php',
      {
        method: 'GET',
        credentials: 'include',
      }
    )
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';
        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
        setIsAuthenticated(!!authed);
      })
      .catch((err) => {
        console.error('Error fetching auth state:', err);
        setIsAuthed(false);
        setAuthMode('none');
        setIsAuthenticated(false);
      })
      .finally(() => setAuthChecked(true));
  }, []);

  // Helper: open in same tab (web) or via Linking (native)
  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  // Working list (filters + sort applied)
  const combined = useMemo(() => {
    if (!suggested && !pool) return [];

    const lowerName = (u) =>
      String(u?.Username || '').trim().toLowerCase();

    let base;

    // If suggestedOnly: only suggested users
    if (suggestedOnly) {
      base = suggested.slice();
    } else {
      // Otherwise: main list = pool minus any usernames already in suggested
      const suggestedSet = new Set(
        suggested.map((u) => lowerName(u))
      );
      base = pool.filter(
        (p) => !suggestedSet.has(lowerName(p))
      );
    }

    // Search by username
    const q = query.trim().toLowerCase();
    if (q) {
      base = base.filter((i) =>
        String(i.Username || '').toLowerCase().includes(q)
      );
    }

    // Category
    if (selectedCategory !== 'All') {
      base = base.filter(
        (i) =>
          String(i.ContentCategories).trim() === selectedCategory
      );
    }

    // Follower bucket
    if (bucket) {
      base = base.filter((i) => {
        const n = parseFollowers(i.PlatformFollowers);
        return n >= bucket.min && n < bucket.max;
      });
    }

    // Photo
    if (hasPhoto) {
      base = base.filter((i) => !!i.ProfileImage);
    }

    // Sort
    if (sort === 'followers_desc') {
      base.sort(
        (a, b) =>
          parseFollowers(b.PlatformFollowers) -
          parseFollowers(a.PlatformFollowers)
      );
    } else if (sort === 'followers_asc') {
      base.sort(
        (a, b) =>
          parseFollowers(a.PlatformFollowers) -
          parseFollowers(b.PlatformFollowers)
      );
    } else if (sort === 'az') {
      base.sort((a, b) =>
        String(a.Username).localeCompare(String(b.Username))
      );
    } else if (sort === 'shuffle') {
      for (let i = base.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [base[i], base[j]] = [base[j], base[i]];
      }
    }

    // Cap to max 5 creators with >= 50k followers
    const out = [];
    let big = 0;

    for (const item of base) {
      const n = parseFollowers(item.PlatformFollowers);
      if (n >= 50000) {
        if (big >= 15) continue; // skip extras above the cap
        big++;
      }
      out.push(item);
      if (out.length >= MAX_RENDER) break;
    }

    return out;
  }, [
    suggested,
    pool,
    suggestedOnly,
    selectedCategory,
    bucket,
    hasPhoto,
    sort,
    query,
  ]);

  const showEmpty = combined.length === 0;
  const deviceW = Dimensions.get('window').width;

  // "For you" paging logic (4 at a time)
  const totalSuggested = !suggestedOnly ? suggested.length : 0;
  const safeStart =
    totalSuggested === 0
      ? 0
      : Math.min(suggestStart, Math.max(totalSuggested - 1, 0));
  const visibleSuggested =
    totalSuggested > 0
      ? suggested.slice(
          safeStart,
          safeStart + FOR_YOU_PAGE_SIZE
        )
      : [];
  const canGoPrev = safeStart > 0;
  const canGoNext = safeStart + FOR_YOU_PAGE_SIZE < totalSuggested;

  const handlePrevSuggested = () => {
    if (!canGoPrev) return;
    setSuggestStart((idx) =>
      Math.max(idx - FOR_YOU_PAGE_SIZE, 0)
    );
  };

  const handleNextSuggested = () => {
    if (!canGoNext) return;
    setSuggestStart((idx) =>
      Math.min(
        idx + FOR_YOU_PAGE_SIZE,
        Math.max(totalSuggested - FOR_YOU_PAGE_SIZE, 0)
      )
    );
  };

  // LOADING STATE with SIDENAV
  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.appShell}>
          <View style={styles.shellRow}>
            {/* SIDENAV */}
            <View style={styles.sideNav}>
              <LinearGradient
                colors={['#050509', '#050505', '#000000']}
                style={styles.sideNavInner}
              >
                {/* subtle glow blob */}
                <LinearGradient
                  colors={[
                    'rgba(253,240,93,0.08)',
                    'rgba(0,0,0,0)',
                  ]}
                  style={styles.sideNavGlow}
                  start={{ x: 0.1, y: 0 }}
                  end={{ x: 1, y: 1 }}
                />

                {/* TOP: Logo + wordmark */}
                <View style={styles.sideNavHeader}>
                  <Image
                    source={{
                      uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                    }}
                    style={styles.sideNavLogoImage}
                    resizeMode="contain"
                  />
                  <View>
                    <Text style={styles.logoWordmark}>
                      <Text style={{ fontWeight: '300' }}>collab</Text>
                      bay
                      <Text style={styles.logoDot}>.</Text>
                    </Text>
                    <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                  </View>
                </View>

                {/* MAIN NAV SECTION */}
                <View style={styles.sideNavSection}>
                  {/* Username pill */}
                  <LinearGradient
                    colors={['#111111', '#2b2b2b']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.usernamePill}
                  >
                    <Text
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      style={styles.usernamePillText}
                    >
                      {userData.Username
                        ? ` ${userData.Username}`
                        : 'Welcome Back'}
                    </Text>
                  </LinearGradient>

                  {/* Home → MyPanel */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => navigation.navigate('MyPanel')}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="home-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>Home</Text>
                      </View>
                    </View>
                  </TouchableOpacity>

                  {/* My Offers → MyOffers */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => navigation.navigate('MyOffers')}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="pricetags-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>My Offers</Text>
                      </View>
                    </View>
                  </TouchableOpacity>

                  {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => {
                      if (authChecked && !isAuthed) {
                        openInSameTab(
                          'https://collabbayadmin.com/plans'
                        );
                      } else {
                        navigation.navigate('MyCalendar');
                      }
                    }}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="calendar-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>My Calendar</Text>
                      </View>
                      {authChecked && !isAuthed && (
                        <Ionicons
                          name="lock-closed-outline"
                          size={16}
                          color="#FDF05D"
                          style={styles.lockIcon}
                        />
                      )}
                    </View>
                  </TouchableOpacity>

                  {/* Browse Creators → BrowseCreators */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => navigation.navigate('BrowseCreators')}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="people-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>Browse Creators</Text>
                      </View>
                    </View>
                  </TouchableOpacity>

                  {/* Activity → MyActivityFeed (LOCKED if NOT authed) */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => {
                      if (authChecked && !isAuthed) {
                        openInSameTab(
                          'https://collabbayadmin.com/plans'
                        );
                      } else {
                        navigation.navigate('MyActivityFeed');
                      }
                    }}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="chatbubbles-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>Activity</Text>
                      </View>
                      {authChecked && !isAuthed && (
                        <Ionicons
                          name="lock-closed-outline"
                          size={16}
                          color="#FDF05D"
                          style={styles.lockIcon}
                        />
                      )}
                    </View>
                  </TouchableOpacity>

                  {/* Account → BusinessAccountPage */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() =>
                      navigation.navigate('BusinessAccountPage')
                    }
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="person-circle-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>Account</Text>
                      </View>
                    </View>
                  </TouchableOpacity>
                </View>

                {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
                <View style={styles.sideNavSection}>
                  {/* Peer Map */}
                  <TouchableOpacity
                    style={styles.peerMapCard}
                    activeOpacity={0.9}
                    onPress={() => openInSameTab('/venues')}
                  >
                    <LinearGradient
                      colors={[
                        'rgba(255,255,255,0.08)',
                        'rgba(0,0,0,0.4)',
                      ]}
                      style={styles.peerMapInner}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.peerMapContent}>
                        <Ionicons
                          name="map-outline"
                          size={18}
                          color="#FDF05D"
                          style={{ marginRight: 8 }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.peerMapTitle}>Peer Map</Text>
                          <Text style={styles.peerMapSubtitle}>
                            View 100+ venues on Collabbay
                          </Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>

                  {/* Subscribe CTA — only if NOT subscriber */}
                  {!isSubscriber && (
                    <TouchableOpacity
                      style={styles.subscribeCard}
                      activeOpacity={0.9}
                      onPress={() =>
                        openInSameTab(
                          'https://collabbayadmin.com/plans'
                        )
                      }
                    >
                      <LinearGradient
                        colors={['#FDF05D', '#fbbf24']}
                        style={styles.subscribeGradient}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                      >
                        <View style={styles.subscribeContent}>
                          <Ionicons
                            name="rocket-outline"
                            size={20}
                            color="#000"
                            style={{ marginRight: 8 }}
                          />
                          <View style={{ flex: 1 }}>
                            <Text style={styles.subscribeTitle}>
                              Subscribe & unlock
                            </Text>
                            <Text style={styles.subscribeSubtitle}>
                              Unlock Activity & Calendar, plus more.
                            </Text>
                          </View>
                        </View>
                      </LinearGradient>
                    </TouchableOpacity>
                  )}
                </View>

                {/* BOTTOM: FAQ + Help */}
                <View style={styles.sideNavBottom}>
                  <TouchableOpacity
                    style={styles.bottomLink}
                    onPress={() =>
                      openInSameTab(
                        'https://collabbayadmin.com/FAQs'
                      )
                    }
                  >
                    <Ionicons
                      name="help-circle-outline"
                      size={18}
                      color="#f5f5f5"
                      style={{ marginRight: 8 }}
                    />
                    <View>
                      <Text style={styles.bottomLinkText}>FAQs</Text>
                      <Text style={styles.bottomLinkCaption}>
                        Common questions answered
                      </Text>
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.bottomLink}
                    onPress={() =>
                      Linking.openURL('mailto:info@collab-bay.com')
                    }
                  >
                    <Ionicons
                      name="headset-outline"
                      size={18}
                      color="#f5f5f5"
                      style={{ marginRight: 8 }}
                    />
                    <View>
                      <Text style={styles.bottomLinkText}>Help</Text>
                      <Text style={styles.bottomLinkCaption}>
                        We&apos;re here 24/7
                      </Text>
                    </View>
                  </TouchableOpacity>
                </View>
              </LinearGradient>
            </View>

            {/* MAIN AREA LOADER */}
            <View style={styles.mainArea}>
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="black" />
                <Text style={styles.loadingText}>
                  Loading creators…
                </Text>
              </View>
            </View>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  // MAIN RENDER with SIDENAV
  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.appShell}>
        <View style={styles.shellRow}>
          {/* SIDENAV */}
          <View style={styles.sideNav}>
            <LinearGradient
              colors={['#050509', '#050505', '#000000']}
              style={styles.sideNavInner}
            >
              {/* subtle glow blob */}
              <LinearGradient
                colors={[
                  'rgba(253,240,93,0.08)',
                  'rgba(0,0,0,0)',
                ]}
                style={styles.sideNavGlow}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 1, y: 1 }}
              />

              {/* TOP: Logo + wordmark */}
              <View style={styles.sideNavHeader}>
                <Image
                  source={{
                    uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                  }}
                  style={styles.sideNavLogoImage}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.logoWordmark}>
                    <Text style={{ fontWeight: '300' }}>collab</Text>
                    bay
                    <Text style={styles.logoDot}>.</Text>
                  </Text>
                  <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                </View>
              </View>

              {/* MAIN NAV SECTION */}
              <View style={styles.sideNavSection}>
                {/* Username pill */}
                <LinearGradient
                  colors={['#111111', '#2b2b2b']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.usernamePill}
                >
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={styles.usernamePillText}
                  >
                    {userData.Username
                      ? ` ${userData.Username}`
                      : 'Welcome Back'}
                  </Text>
                </LinearGradient>

                {/* Home → MyPanel */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyPanel')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="home-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Home</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Offers → MyOffers */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyOffers')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="pricetags-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Offers</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab(
                        'https://collabbayadmin.com/plans'
                      );
                    } else {
                      navigation.navigate('MyCalendar');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Calendar</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Browse Creators → BrowseCreators */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('BrowseCreators')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="people-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Browse Creators</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* Activity → MyActivityFeed (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab(
                        'https://collabbayadmin.com/plans'
                      );
                    } else {
                      navigation.navigate('MyActivityFeed');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="chatbubbles-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Activity</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Account → BusinessAccountPage */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() =>
                    navigation.navigate('BusinessAccountPage')
                  }
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="person-circle-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Account</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              </View>

              {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
              <View style={styles.sideNavSection}>
                {/* Peer Map */}
                <TouchableOpacity
                  style={styles.peerMapCard}
                  activeOpacity={0.9}
                  onPress={() => openInSameTab('/venues')}
                >
                  <LinearGradient
                    colors={[
                      'rgba(255,255,255,0.08)',
                      'rgba(0,0,0,0.4)',
                    ]}
                    style={styles.peerMapInner}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <View style={styles.peerMapContent}>
                      <Ionicons
                        name="map-outline"
                        size={18}
                        color="#FDF05D"
                        style={{ marginRight: 8 }}
                      />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.peerMapTitle}>Peer Map</Text>
                        <Text style={styles.peerMapSubtitle}>
                          View 100+ venues on Collabbay
                        </Text>
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>

                {/* Subscribe CTA — only if NOT subscriber */}
                {!isSubscriber && (
                  <TouchableOpacity
                    style={styles.subscribeCard}
                    activeOpacity={0.9}
                    onPress={() =>
                      openInSameTab(
                        'https://collabbayadmin.com/plans'
                      )
                    }
                  >
                    <LinearGradient
                      colors={['#FDF05D', '#fbbf24']}
                      style={styles.subscribeGradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.subscribeContent}>
                        <Ionicons
                          name="rocket-outline"
                          size={20}
                          color="#000"
                          style={{ marginRight: 8 }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.subscribeTitle}>
                            Subscribe & unlock
                          </Text>
                          <Text style={styles.subscribeSubtitle}>
                            Unlock Activity & Calendar, plus more.
                          </Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>

              {/* BOTTOM: FAQ + Help */}
              <View style={styles.sideNavBottom}>
                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() =>
                    openInSameTab(
                      'https://collabbayadmin.com/FAQs'
                    )
                  }
                >
                  <Ionicons
                    name="help-circle-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>FAQs</Text>
                    <Text style={styles.bottomLinkCaption}>
                      Common questions answered
                    </Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() =>
                    Linking.openURL('mailto:info@collab-bay.com')
                  }
                >
                  <Ionicons
                    name="headset-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>Help</Text>
                    <Text style={styles.bottomLinkCaption}>
                      We&apos;re here 24/7
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>

          {/* MAIN AREA (EXISTING CONTENT) */}
          <View style={styles.mainArea}>
            <ScrollView
              contentContainerStyle={styles.scrollContainer}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={onRefresh}
                />
              }
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.contentWrapper}>
                {/* Header row: back + close */}
                <View style={styles.pageHeaderRow}>
                  <TouchableOpacity
                    onPress={() => navigation.goBack()}
                    style={styles.backButton}
                  >
                    <Ionicons name="arrow-back" size={20} color="black" />
                    <Text style={styles.backButtonText}>Back</Text>
                  </TouchableOpacity>

                  <View style={styles.headerRight}>
                    <TouchableOpacity
                      onPress={() =>
                        Linking.openURL('https://collabbayadmin.com')
                      }
                      style={styles.closeButton}
                    >
                      <Ionicons name="close" size={22} color="#000" />
                    </TouchableOpacity>
                  </View>
                </View>

                {/* Hero banner */}
                <View style={styles.bannerCard}>
                  <View style={{ flex: 1, paddingRight: 10 }}>
                    <Text style={styles.header}>
                      Looking for more bookings?
                    </Text>
                    <Text style={styles.subhead}>
                      Propose invites to creators through collabbay — filter by
                      category, followers and more.
                    </Text>
                  </View>
                  <Ionicons
                    name="megaphone-outline"
                    size={26}
                    color="#000"
                  />
                </View>

                {/* Pro tip (dynamic) */}
                {Math.random() < 0.3 && (
                  <View style={styles.inviteInfoBox}>
                    <Text style={styles.inviteInfoHeader}>💡 Pro tip</Text>
                    <Text style={styles.inviteInfoText}>
                      Most venues start by sending just a few invites — it’s a
                      quick way to get the ball rolling and build early traction
                      with creators.
                    </Text>
                  </View>
                )}

                {/* Search */}
                <View style={styles.searchBar}>
                  <Ionicons
                    name="search"
                    size={18}
                    color="#666"
                    style={{ marginRight: 8 }}
                  />
                  <TextInput
                    value={query}
                    onChangeText={setQuery}
                    placeholder="Search username..."
                    placeholderTextColor="#999"
                    style={styles.searchInput}
                    autoCapitalize="none"
                  />
                  {query ? (
                    <TouchableOpacity onPress={() => setQuery('')}>
                      <Ionicons
                        name="close-circle"
                        size={18}
                        color="#999"
                      />
                    </TouchableOpacity>
                  ) : null}
                </View>

                {/* “For you” Suggested Strip — 4 at a time with arrows */}
                {!suggestedOnly &&
                  visibleSuggested.length > 0 && (
                    <>
                      <View style={styles.sectionHeader}>
                        <Ionicons
                          name="star-outline"
                          size={18}
                          color="#000"
                        />
                        <Text style={styles.sectionTitle}>For you</Text>
                      </View>

                      <View style={styles.forYouRow}>
                        <TouchableOpacity
                          onPress={handlePrevSuggested}
                          disabled={!canGoPrev}
                          style={[
                            styles.arrowButton,
                            !canGoPrev && styles.arrowButtonDisabled,
                          ]}
                          activeOpacity={0.8}
                        >
                          <Ionicons
                            name="chevron-back"
                            size={20}
                            color={canGoPrev ? '#111' : '#b3b3b3'}
                          />
                        </TouchableOpacity>

                        <View style={styles.forYouCardsRow}>
                          {visibleSuggested.map((cr, idx) => (
                            <TouchableOpacity
                              key={`sg-${cr.Username}-${safeStart + idx}`}
                              onPress={() => {
                                if (!isAuthenticated) {
                                  Linking.openURL(
                                    'https://collabbayadmin.com/plans'
                                  );
                                } else {
                                  navigation.navigate('InviteCreator', {
                                    Username: cr.Username,
                                  });
                                }
                              }}
                              style={[
                                styles.suggestCard,
                                { width: Math.min(260, deviceW * 0.7) },
                              ]}
                              activeOpacity={0.9}
                            >
                              {cr.ProfileImage ? (
                                <Image
                                  source={{ uri: cr.ProfileImage }}
                                  style={styles.suggestImage}
                                />
                              ) : (
                                <View
                                  style={[
                                    styles.suggestImage,
                                    {
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    },
                                  ]}
                                >
                                  <Ionicons
                                    name="person-circle-outline"
                                    size={40}
                                    color="#ccc"
                                  />
                                </View>
                              )}
                              <View style={styles.suggestOverlay}>
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Text
                                    numberOfLines={1}
                                    style={styles.suggestName}
                                  >
                                    {!isAuthenticated ? (
                                      <>
                                        {cr.Username?.charAt(0)}
                                        <Text style={styles.blurredText}>
                                          {cr.Username?.slice(1)}
                                        </Text>
                                      </>
                                    ) : (
                                      cr.Username
                                    )}
                                  </Text>
                                  {TOP_BADGE.has(
                                    String(cr.CreatorRating || '')
                                      .toUpperCase()
                                  ) && (
                                    <View style={styles.topIconPill}>
                                      <Ionicons
                                        name="ribbon"
                                        size={12}
                                        color="#000"
                                      />
                                    </View>
                                  )}
                                  <View
                                    style={[
                                      styles.badge,
                                      styles.badgeMuted,
                                      { marginLeft: 6 },
                                    ]}
                                  >
                                    <Text style={styles.badgeText}>
                                      Suggested
                                    </Text>
                                  </View>
                                </View>
                                <Text
                                  numberOfLines={1}
                                  style={styles.suggestMeta}
                                >
                                  {formatBucket(
                                    cr.PlatformFollowers
                                  )}{' '}
                                  •{' '}
                                  {String(
                                    cr.ContentCategories || ''
                                  ).trim() || 'Creator'}{' '}
                                  Creator
                                </Text>
                              </View>
                            </TouchableOpacity>
                          ))}
                        </View>

                        <TouchableOpacity
                          onPress={handleNextSuggested}
                          disabled={!canGoNext}
                          style={[
                            styles.arrowButton,
                            !canGoNext && styles.arrowButtonDisabled,
                          ]}
                          activeOpacity={0.8}
                        >
                          <Ionicons
                            name="chevron-forward"
                            size={20}
                            color={canGoNext ? '#111' : '#b3b3b3'}
                          />
                        </TouchableOpacity>
                      </View>
                    </>
                  )}

                {/* Sort row */}
                <View style={styles.rowWrap}>
                  <Chip
                    label="Followers ↑"
                    active={sort === 'followers_desc'}
                    onPress={() => setSort('followers_desc')}
                  />
                  <Chip
                    label="Followers ↓"
                    active={sort === 'followers_asc'}
                    onPress={() => setSort('followers_asc')}
                  />
                  <Chip
                    label="A–Z"
                    active={sort === 'az'}
                    onPress={() => setSort('az')}
                  />
                  <Chip
                    label="Shuffle"
                    active={sort === 'shuffle'}
                    onPress={() => setSort('shuffle')}
                  />
                  <Chip
                    label="Reset"
                    active={false}
                    onPress={() => {
                      setSelectedCategory('All');
                      setBucket(null);
                      setHasPhoto(false);
                      setSuggestedOnly(false);
                      setQuery('');
                      setSort('shuffle');
                    }}
                  />
                </View>

                {/* Filters */}
                <Text style={styles.filterHeading}>Filter</Text>

                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  style={{ marginBottom: 8 }}
                >
                  {categories.map((cat, i) => (
                    <Chip
                      key={`${cat}-${i}`}
                      label={cat}
                      active={selectedCategory === cat}
                      onPress={() => setSelectedCategory(cat)}
                    />
                  ))}
                </ScrollView>

                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  style={{ marginBottom: 8 }}
                >
                  {FOLLOWER_BUCKETS.map((b) => (
                    <Chip
                      key={b.key}
                      label={b.key}
                      active={bucket?.key === b.key}
                      onPress={() =>
                        setBucket(
                          bucket?.key === b.key ? null : b
                        )
                      }
                    />
                  ))}
                </ScrollView>

                <View style={styles.toggleRow}>
                  <Toggle
                    active={hasPhoto}
                    icon="image-outline"
                    label="Has photo"
                    onPress={() => setHasPhoto((v) => !v)}
                  />
                  <Toggle
                    active={suggestedOnly}
                    icon="star-outline"
                    label="Suggested only"
                    onPress={() => setSuggestedOnly((v) => !v)}
                  />
                  <View style={{ flex: 1 }} />
                  <Text style={styles.countText}>
                    Showing {combined.length} / {MAX_RENDER}
                  </Text>
                </View>

                {/* Main list */}
                <View style={styles.sectionHeader}>
                  <Ionicons
                    name="people-outline"
                    size={18}
                    color="#000"
                  />
                  <Text style={styles.sectionTitle}>
                    {suggestedOnly
                      ? 'Suggested creators'
                      : 'More creators'}
                  </Text>
                </View>

                {showEmpty ? (
                  <View
                    style={{
                      alignItems: 'center',
                      marginTop: 24,
                    }}
                  >
                    <Text style={styles.emptyMsg}>
                      {err ||
                        'No matches. Try loosening filters or post an offer to invite creators.'}
                    </Text>
                    <TouchableOpacity
                      style={styles.ctaBtn}
                      onPress={() =>
                        navigation.navigate('CreateBrandListing')
                      }
                    >
                      <Text style={styles.ctaText}>
                        Create an offer{' '}
                        <Ionicons
                          name="arrow-forward"
                          size={18}
                          color="#000"
                        />
                      </Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View style={styles.cardsGrid}>
                    {combined.map((cr, idx) => (
                      <CreatorCard
                        key={`cr-${cr.Username}-${idx}`}
                        creator={cr}
                        isAuthenticated={isAuthenticated}
                        isWide={deviceW >= 900}
                      />
                    ))}
                  </View>
                )}

                {!showEmpty && combined.length < MAX_RENDER && (
                  <Text style={styles.nudge}>
                    Tip: widen filters to see more creators (up to{' '}
                    {MAX_RENDER} shown).
                  </Text>
                )}
              </View>
            </ScrollView>
          </View>
        </View>
      </View>
    </SafeAreaView>
  );
}

function CreatorCard({ creator, isAuthenticated, isWide }) {
  const navigation = useNavigation();
  const top = TOP_BADGE.has(
    String(creator.CreatorRating || '').toUpperCase()
  );

  const handlePress = () => {
    if (!isAuthenticated) {
      Linking.openURL('https://collabbayadmin.com/plans');
    } else {
      navigation.navigate('InviteCreator', {
        Username: creator.Username,
      });
    }
  };

  return (
    <TouchableOpacity
      style={[styles.card, isWide && styles.cardWide]}
      onPress={handlePress}
      activeOpacity={0.9}
    >
      <View style={styles.row}>
        <View style={{ position: 'relative' }}>
          {creator.ProfileImage ? (
            <Image
              source={{ uri: creator.ProfileImage }}
              style={styles.image}
            />
          ) : (
            <View
              style={[
                styles.image,
                { justifyContent: 'center', alignItems: 'center' },
              ]}
            >
              <Ionicons
                name="person-circle-outline"
                size={40}
                color="#ccc"
              />
            </View>
          )}
          <View pointerEvents="none" style={styles.haloOverlay} />
        </View>
        <View style={styles.details}>
          <View
            style={{ flexDirection: 'row', alignItems: 'center' }}
          >
            <Text style={styles.name} numberOfLines={1}>
              {!isAuthenticated ? (
                <>
                  {creator.Username?.charAt(0)}
                  <Text style={styles.blurredText}>
                    {creator.Username?.slice(1)}
                  </Text>
                </>
              ) : (
                creator.Username
              )}
            </Text>
            {top && (
              <View style={styles.topIconPill}>
                <Ionicons name="ribbon" size={12} color="#000" />
              </View>
            )}
            {creator.isSuggested && (
              <View
                style={[
                  styles.badge,
                  styles.badgeMuted,
                  { marginLeft: 6 },
                ]}
              >
                <Text style={styles.badgeText}>Suggested</Text>
              </View>
            )}
          </View>
          <Text style={styles.followers}>
            {formatBucket(creator.PlatformFollowers)} followers
          </Text>
          <Text style={styles.category} numberOfLines={1}>
            {String(creator.ContentCategories || '').trim() ||
              'Creator'}
          </Text>
        </View>
        <TouchableOpacity style={styles.viewButton}>
          <Ionicons name="arrow-forward" size={20} color="#000" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
}

function Chip({ label, active, onPress }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[styles.chip, active && styles.activeChip]}
      activeOpacity={0.85}
    >
      <Text style={styles.chipText}>{label}</Text>
    </TouchableOpacity>
  );
}

function Toggle({ active, onPress, icon, label }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[styles.toggleBtn, active && styles.activeToggle]}
      activeOpacity={0.9}
    >
      <Ionicons name={icon} size={16} color="#000" />
      <Text style={styles.toggleText}>{label}</Text>
    </TouchableOpacity>
  );
}

function formatBucket(countStr) {
  const n = parseFollowers(countStr);
  if (n < 25000) return '10k–25k';
  if (n < 50000) return '25k–50k';
  if (n < 100000) return '50k–100k';
  if (n < 300000) return '100k–300k';
  return '300k+';
}

const styles = StyleSheet.create({
  /* APP SHELL */
  safeArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  appShell: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  /* SIDE NAV */
  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },

  // Peer Map card
  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },

  // Subscribe CTA
  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },

  // BOTTOM LINKS
  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },

  // MAIN AREA
  mainArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  scrollContainer: {
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
  },

  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  // Loading overlay in main area (used in loading state)
  loadingContainer: {
    flex: 1,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 8,
    fontSize: 13,
    color: '#6b7280',
  },

  // PAGE HEADER
  pageHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#eee',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
  },
  backButtonText: {
    marginLeft: 6,
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
  },
  closeButton: {
    backgroundColor: '#fff',
    padding: 6,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 2,
  },

  // Hero banner
  bannerCard: {
    backgroundColor: '#FDF05D',
    borderRadius: 18,
    padding: 16,
    marginBottom: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  header: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 6,
    color: '#000',
  },
  subhead: {
    fontSize: 14,
    color: '#555',
    fontWeight: '500',
  },

  // Pro tip box
  inviteInfoBox: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#ececec',
    shadowColor: '#000',
    shadowOpacity: 0.03,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  inviteInfoHeader: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
    marginBottom: 4,
  },
  inviteInfoText: {
    fontSize: 12,
    color: '#555',
  },

  // Search
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#e6e6e6',
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  searchInput: { flex: 1, fontSize: 14, color: '#111' },

  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 8,
  },
  sectionTitle: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '800',
    color: '#000',
  },

  // For you row
  forYouRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  arrowButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 4,
    borderWidth: 1,
    borderColor: '#e5e5e5',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  arrowButtonDisabled: {
    opacity: 0.35,
  },
  forYouCardsRow: {
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'flex-start',
  },

  // Suggested card
  suggestCard: {
    height: 150,
    borderRadius: 18,
    marginRight: 12,
    overflow: 'hidden',
    backgroundColor: '#f3f3f3',
    borderWidth: 1,
    borderColor: '#eee',
  },
  suggestImage: { width: '100%', height: '100%' },

  // Translucent footer (no blur)
  suggestOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: 'rgba(255,255,255,0.78)',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.6)',
  },

  suggestName: {
    fontSize: 14,
    fontWeight: '800',
    color: '#000',
    maxWidth: '60%',
  },
  suggestMeta: { fontSize: 12, color: '#222', marginTop: 2 },

  // Filters / chips
  rowWrap: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8 },
  filterHeading: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
    marginTop: 2,
    marginBottom: 6,
  },

  chip: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 18,
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#e6e6e6',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  activeChip: {
    backgroundColor: '#fdf05d',
    borderColor: '#f0e04a',
  },
  chipText: { fontSize: 13, fontWeight: '600', color: '#000' },

  toggleRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  toggleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 18,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e6e6e6',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  activeToggle: {
    backgroundColor: '#fdf05d',
    borderColor: '#f0e04a',
  },
  toggleText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#000',
    marginLeft: 6,
  },

  countText: { fontSize: 12, color: '#666', fontWeight: '600' },

  // Main cards grid
  cardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 14,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#eee',
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  cardWide: {
    width: '48%', // 2 per row on desktop
  },
  row: { flexDirection: 'row', alignItems: 'center' },
  image: {
    width: 110,
    height: 110,
    borderRadius: 16,
    backgroundColor: '#e9e9e9',
    marginRight: 14,
  },

  // Halo overlay (no blur)
  haloOverlay: {
    position: 'absolute',
    left: -6,
    right: -6,
    bottom: -6,
    top: -6,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },

  details: { flex: 1 },

  name: { fontSize: 16, fontWeight: '800', color: '#111' },
  followers: { fontSize: 14, fontWeight: '600', color: '#333', marginTop: 4 },
  category: { fontSize: 12, color: '#666', marginTop: 2 },

  viewButton: {
    backgroundColor: '#fdf05d',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
  },

  // Icon-only "Top" pill
  topIconPill: {
    marginLeft: 8,
    backgroundColor: '#fdf05d',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },

  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  badgeMuted: {
    backgroundColor: '#eee',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#000',
  },

  blurredText: {
    color: 'transparent',
    textShadowColor: 'rgba(0,0,0,0.3)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 5,
    userSelect: 'none',
  },

  ctaBtn: {
    backgroundColor: '#fdf05d',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 25,
    marginTop: 10,
  },
  ctaText: { fontWeight: 'bold', color: '#000', fontSize: 16 },

  emptyMsg: { fontSize: 15, color: '#555', textAlign: 'center' },

  nudge: {
    marginTop: 6,
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
  },
});
